import { useState, useEffect, useContext, createContext, forwardRef } from 'react';
import type { PropsWithoutRef, CSSProperties, Ref } from 'react';
import { NimblePicker } from 'emoji-mart';
import type { CategoryName, Data as EmojiData, BackgroundImageFn, PickerProps } from 'emoji-mart';

import { useTheme } from '../../hooks';
import type { BaseProps, DeepPartial, NoChildrenProp } from '../../types';

import StyledEmojiPicker from './EmojiPicker.styles';

type EmojiSet = 'apple' | 'google' | 'twitter' | 'facebook';

export interface EmojiPickerProps extends BaseProps, NoChildrenProp {
  /** Individual emojis size in pixels. */
  emojiSize?: number;
  /** Number of emoji per line in a picker. */
  perLine?: number;
  /** Don't load excluded categories. Accepts [I18n categories keys](https://www.npmjs.com/package/emoji-mart#i18n). */
  exclude?: CategoryName[];
  /** Show footer for picker. */
  showFooter?: boolean;
  /** A Fn that gets called on selecting an emoji from picker. */
  onSelect?: PickerProps['onSelect'];
  /** Ref for the emoji Picker */
  ref?: Ref<HTMLDivElement>;
}

export interface EmojiContextValue {
  /**
   * A Fn that returns that image sheet to use for emojis.
   * If no sprite is provided, native emoji are used.
   * For the source of image please visit this [link](https://www.jsdelivr.com/package/npm/emoji-datasource?version=5.0.1&path=img)
   */
  spriteSrcResolver?: BackgroundImageFn;
  /** Emoji set to be used. */
  set?: EmojiSet;
  /** Data to be used. [link](https://github.com/iamcal/emoji-data).
   * Use this in case you would like to filter out some emoji, one must change the sprite image accordingly. */
  overrideData?: EmojiData;
}

export const EmojiContext = createContext<EmojiContextValue>({});

const emojiDataMap = {
  all: () => import('emoji-mart/data/all.json'),
  apple: () => import('emoji-mart/data/apple.json'),
  google: () => import('emoji-mart/data/google.json'),
  facebook: () => import('emoji-mart/data/facebook.json'),
  twitter: () => import('emoji-mart/data/twitter.json')
} satisfies Record<'all' | EmojiSet, () => Promise<{ default: EmojiData }>>;

const EmojiPicker = forwardRef(function EmojiPicker(
  {
    emojiSize = 24,
    perLine = 9,
    showFooter = true,
    exclude,
    onSelect,
    ...restProps
  }: PropsWithoutRef<EmojiPickerProps>,
  ref: Ref<HTMLDivElement>
) {
  const theme = useTheme();
  const { set, spriteSrcResolver, overrideData } = useContext(EmojiContext);
  const [data, setData] = useState<DeepPartial<EmojiData> | null>(null);

  useEffect(() => {
    // Unmount NimblePicker for changes to take effect
    setData(null);

    setTimeout(async () => {
      if (overrideData) {
        setData(overrideData);
      } else {
        try {
          const { default: emojiData } = await emojiDataMap[set ?? 'all']();
          setData(emojiData);
        } catch (error) {
          return null;
        }
      }
    }, 0);
  }, [set, overrideData]);

  return (
    data && (
      <StyledEmojiPicker
        ref={ref}
        showFooter={showFooter}
        style={{ '--emoji-size': `${emojiSize}px` } as CSSProperties}
      >
        <NimblePicker
          {...restProps}
          set={set}
          native={!spriteSrcResolver}
          backgroundImageFn={spriteSrcResolver}
          data={data as EmojiData}
          emojiSize={emojiSize}
          perLine={perLine}
          exclude={exclude}
          onSelect={onSelect}
          color={theme.base.palette.interactive}
          emoji=''
          title=''
          emojiTooltip
          showSkinTones={showFooter}
          showPreview={false}
        />
      </StyledEmojiPicker>
    )
  );
});

export default EmojiPicker;
